<template>
  <PmModalPure title="Ansicht speichern" @close="emit('close')">
    <div class="PmViewUpdatePure">
      <!-- <div v-if="viewName" class="PmViewUpdatePure-title">
        Ausgewählte Ansicht: <strong>{{ viewName }}</strong>
      </div> -->

      <PmErrorNotificationPure
        v-if="error && errorMessage"
        class="PmViewUpdatePure-notification"
        :message="errorMessage"
        :details="errorDetails"
      />

      <PmAppNotificationPure
        v-if="isPublicView"
        class="PmViewUpdatePure-notification"
        icon="public"
        variant="warning"
      >
        Öffentliche Ansicht — Änderungen wirken sich auf andere Benutzer aus.
      </PmAppNotificationPure>

      <PmAppNotificationPure class="PmViewUpdatePure-notification">
        <template v-if="name === viewName">
          Die Ansicht <strong>„{{ viewName }}”</strong> wird überschrieben.
        </template>

        <template v-if="name !== viewName">
          Die Ansicht <strong>„{{ viewName }}”</strong> wird überschrieben und
          der Name auf <strong>„{{ name }}”</strong> geändert.</template
        >
      </PmAppNotificationPure>

      <form
        :id="`form${uid}`"
        ref="form"
        class="PmViewUpdatePure-form"
        @submit.prevent="update"
      >
        <PmInputPure
          v-model:value="name"
          label="Name"
          placeholder="Namen eingeben"
          class="PmViewUpdatePure-input"
          required
          :disabled="loading"
          :not-allowed="existingViewNames"
          :trigger-validity-check="triggerValidityCheck"
          :validation-messages="{
            notAllowed:
              'Dieser Name existiert bereits für eine andere Ansicht. Bitte wähle einen eindeutigen Namen.',
          }"
        />

        <PmCheckboxPure
          v-if="userCanCreatePublicView"
          v-model:value="formIsPublicView"
          :disabled="loading"
          class="PmViewUpdatePure-public"
          note="Öffentliche Ansichten können von anderen Benutzern verwendet werden. Admins können die Ansicht bearbeiten/löschen."
        >
          Öffentlich
        </PmCheckboxPure>
      </form>
    </div>

    <template #footer>
      <PmButtonListPure align="right">
        <PmButtonPure
          label="Abbrechen"
          :disabled="loading"
          @click="emit('close')"
        />

        <PmButtonPure
          label="Ansicht speichern"
          variant="primary"
          type="submit"
          :form="`form${uid}`"
          :loading="loading"
          :disabled="isSaveDisabled"
        />
      </PmButtonListPure>
    </template>
  </PmModalPure>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

const COMPONENT_NAME = 'PmViewUpdatePure'

export const propTypes = {}

export default defineComponent({
  name: COMPONENT_NAME,
})
</script>

<script setup lang="ts">
import { ref, computed } from 'vue'
import cuid from '@paralleldrive/cuid2'

import PmInputPure from '@/components/basics/PmInput/PmInputPure.vue'
import PmErrorNotificationPure from '@/components/basics/PmErrorNotificationPure.vue'
import PmCheckboxPure from '@/components/basics/PmCheckboxPure.vue'
import PmAppNotificationPure from '@/components/basics/PmAppNotification/PmAppNotificationPure.vue'
import PmModalPure from '@/components/basics/PmModalPure.vue'
import PmButtonListPure from '@/components/basics/PmButtonListPure.vue'
import PmButtonPure from '@/components/basics/PmButtonPure.vue'

export interface Props {
  viewName: string
  loading?: boolean
  error?: boolean
  errorMessage?: string
  errorDetails?: string[]
  userCanCreatePublicView?: boolean
  isPublicView?: boolean
  existingViewNames?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  existingViewNames: () => [],
})

const emit = defineEmits<{
  (event: 'close'): void
  (event: 'update', payload: { name: string; isPublic: boolean }): void
}>()

const uid = cuid.createId()
const name = ref<string>(props.viewName)
const inputNameVisible = ref(false)
const formIsPublicView = ref(props.isPublicView || false)
const triggerValidityCheck = ref(0)

const isSaveDisabled = computed(() => {
  if (props.isPublicView) {
    if (props.userCanCreatePublicView !== true) return true
  }

  return false
})

function update() {
  triggerValidityCheck.value += 1

  emit('update', { name: name.value, isPublic: formIsPublicView.value })
}
</script>

<style lang="scss">
.PmViewUpdatePure {
  $block: &;

  &-title {
    margin-bottom: var(--space-gutters);
  }

  &-notification {
    margin-bottom: var(--space-gutters);
  }

  &-form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
}
</style>
